import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import * as R from 'ramda';
import React, { useMemo, CSSProperties, useRef, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useResizeObserver } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { OptionActions } from './OptionActions';
import { OptionChoicesWithPopover } from './OptionChoicesWithPopover';
import { MENU_MOBILE_BREAKPOINT } from '../../constants';
import { Option, OptionType, OptionValue } from '../../types';

type OptionHeaderProps = {
  active?: boolean;
  className?: string;
  hideVat?: boolean;
  onClick?: MouseEventHandler;
  onCopy?: (optionId: string) => void;
  option: Option;
  orderDisabledChoicesFirst?: boolean;
  primaryLanguage: Language;
  style?: CSSProperties;
};

const optionTypes: { [key in OptionType]: string } = {
  bool: 'menu-option.switch',
  choice: 'menu-option.single-choice',
  multi_choice: 'menu-option.multi-choice',
};

const Root = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  user-select: none;
  cursor: pointer;
`;

const Header = styled.div<{ active?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 104px;
  padding: 0 24px;
  background-color: ${({ active }) => (active ? colors.wolt8 : cbSemanticCssVars.surfaceMain)};
  transition: background-color 100ms;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding: 0 16px;
  }

  &:hover {
    background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  margin-inline-end: 24px;
  overflow: hidden;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-end: 16px;
  }
`;

const NameAndChoices = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
`;

const NameAndTags = styled.div`
  display: flex;
  margin-block-end: 4px;
  flex-direction: row;
  align-items: center;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Name = styled(T.Body3).attrs({ fontWeight: 'bold' })`
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${cbSemanticCssVars.textPrimary};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-block-end: 4px;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const StyledTag = styled(Tag)`
  &:not(:first-child) {
    margin-inline-start: 12px;
  }
`;

const Right = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
`;

export const OptionHeader = ({
  style,
  className,
  active,
  hideVat = false,
  onCopy,
  option,
  orderDisabledChoicesFirst = false,
  onClick,
  primaryLanguage,
}: OptionHeaderProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { currency } = useCountryConfigContext();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const numOfDisabledChoices = useMemo(
    () =>
      option.values.reduce((acc: number, choice: OptionValue) => {
        return choice.enabled.enabled === false ? acc + 1 : acc;
      }, 0),
    [option.values],
  );

  const orderedOptionChoices = useMemo(
    () =>
      orderDisabledChoicesFirst
        ? R.sort((a, b) => (a.enabled === b.enabled ? 0 : a.enabled ? 1 : -1), option.values)
        : [...option.values],
    [option.values, orderDisabledChoicesFirst],
  );

  const { width } = useResizeObserver(rootRef);

  /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  return (
    <Root style={style} className={className} onClick={onClick} ref={rootRef}>
      <Header active={active}>
        <Left>
          <NameAndChoices>
            <NameAndTags>
              <Name dir="auto" lang={primaryLanguage}>
                {localize(option.name, primaryLanguage)}
              </Name>
              <TagsContainer>
                <StyledTag size={isNarrowView ? 'tiny' : 'small'} uppercase={!isNarrowView} variant="lightBlack">
                  <FormattedMessage id={optionTypes[option.type]} />
                </StyledTag>
                {numOfDisabledChoices > 0 && (
                  <StyledTag size={isNarrowView ? 'tiny' : 'small'} uppercase={!isNarrowView} variant="lightRed">
                    <FormattedMessage
                      id="menu-option.disabled-choices-with-count"
                      values={{ count: numOfDisabledChoices }}
                    />
                  </StyledTag>
                )}
              </TagsContainer>
            </NameAndTags>

            <OptionChoicesWithPopover
              onClick={onClick}
              hideVat={hideVat}
              optionChoices={orderedOptionChoices}
              primaryLanguage={primaryLanguage}
              currency={currency}
            />
          </NameAndChoices>
        </Left>

        {!isSharedVenue && menuPermissions.contentEdit && (
          <Right>
            <OptionActions onCopy={onCopy} optionId={option.id} primaryLanguage={primaryLanguage} />
          </Right>
        )}
      </Header>
    </Root>
  );
};
